import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { apiClient, type ManagedImportProfile } from "../../api/client";
import { errorMessage } from "./errors";

export function ImportProfileManager() {
  const [profiles, setProfiles] = useState<ManagedImportProfile[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [definitionText, setDefinitionText] = useState("{}");
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = profiles.find((item) => item.version_id === selectedId) ?? profiles[0];
  const latest = useMemo(
    () =>
      profiles
        .filter((item) => item.profile_id === selected?.profile_id)
        .sort((a, b) => b.version - a.version)[0],
    [profiles, selected?.profile_id],
  );

  const load = useCallback(async () => {
    const values = await apiClient.listManagedImportProfiles();
    setProfiles(values);
    setSelectedId((current) =>
      values.some((item) => item.version_id === current) ? current : (values[0]?.version_id ?? ""),
    );
  }, []);

  useEffect(() => {
    load().catch((error: unknown) => setMessage(errorMessage(error)));
  }, [load]);

  useEffect(() => {
    if (selected) setDefinitionText(JSON.stringify(selected.definition, null, 2));
  }, [selected?.version_id]);

  async function createVersion(event: FormEvent) {
    event.preventDefault();
    if (!selected || !latest) return;
    await perform(async () => {
      const definition = parseDefinition(definitionText);
      definition.code = selected.code;
      definition.report_type = selected.report_type;
      definition.version = latest.version + 1;
      const created = await apiClient.createImportProfileVersion(selected.profile_id, definition);
      setMessage(`已创建 ${created.code}@${created.version} 草稿。`);
      await load();
      setSelectedId(created.version_id);
    });
  }

  async function createProfile() {
    if (!selected || !newCode.trim() || !newName.trim()) return;
    await perform(async () => {
      const definition = parseDefinition(definitionText);
      definition.code = newCode.trim().toLowerCase();
      definition.version = 1;
      const created = await apiClient.createImportProfile({
        code: newCode.trim(),
        display_name: newName.trim(),
        definition,
      });
      setMessage(`已创建 ${created.code}@1 草稿。`);
      setNewCode("");
      setNewName("");
      await load();
      setSelectedId(created.version_id);
    });
  }

  async function changeState(action: "publish" | "disable") {
    if (!selected) return;
    await perform(async () => {
      const updated =
        action === "publish"
          ? await apiClient.publishImportProfile(selected.version_id)
          : await apiClient.disableImportProfile(selected.version_id);
      setMessage(
        `${updated.code}@${updated.version} 已${action === "publish" ? "发布" : "停用"}。`,
      );
      await load();
    });
  }

  async function perform(action: () => Promise<void>) {
    setBusy(true);
    setMessage("");
    try {
      await action();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flow-panel" id="profiles">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">不可变版本</span>
          <h2>导入模板管理</h2>
        </div>
        <span className="phase-badge">{profiles.length} 个版本</span>
      </div>
      {message && (
        <p className="status-banner" role="status">
          {message}
        </p>
      )}
      <div className="admin-two-column">
        <div className="admin-detail">
          <label>
            选择模板版本
            <select
              onChange={(event) => setSelectedId(event.target.value)}
              value={selected?.version_id ?? ""}
            >
              {profiles.map((item) => (
                <option key={item.version_id} value={item.version_id}>
                  {item.code}@{item.version} · {item.state}
                </option>
              ))}
            </select>
          </label>
          {selected && (
            <>
              <dl className="admin-facts">
                <div>
                  <dt>报表类型</dt>
                  <dd>{selected.report_type}</dd>
                </div>
                <div>
                  <dt>定义摘要</dt>
                  <dd>{selected.definition_sha256.slice(0, 12)}…</dd>
                </div>
              </dl>
              <div className="admin-inline-actions">
                <button
                  disabled={busy || selected.state !== "draft"}
                  onClick={() => void changeState("publish")}
                  type="button"
                >
                  发布草稿
                </button>
                <button
                  className="danger-outline-button"
                  disabled={busy || selected.state === "disabled"}
                  onClick={() => void changeState("disable")}
                  type="button"
                >
                  停用版本
                </button>
              </div>
            </>
          )}
          <div className="admin-inline-actions">
            <input
              aria-label="新模板代码"
              onChange={(event) => setNewCode(event.target.value)}
              placeholder="新模板代码"
              value={newCode}
            />
            <input
              aria-label="新模板名称"
              onChange={(event) => setNewName(event.target.value)}
              placeholder="显示名称"
              value={newName}
            />
            <button
              disabled={busy || !selected || !newCode.trim() || !newName.trim()}
              onClick={() => void createProfile()}
              type="button"
            >
              从当前定义创建
            </button>
          </div>
        </div>
        <form className="admin-form" onSubmit={createVersion}>
          <h3>定义编辑器</h3>
          <p className="panel-copy">保存时会固定代码、报表类型和下一个连续版本号。</p>
          <textarea
            aria-label="导入模板 JSON 定义"
            onChange={(event) => setDefinitionText(event.target.value)}
            rows={16}
            spellCheck={false}
            value={definitionText}
          />
          <button disabled={busy || !selected} type="submit">
            创建下一版草稿
          </button>
        </form>
      </div>
    </section>
  );
}

function parseDefinition(value: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("模板定义必须是 JSON 对象。");
  }
  return { ...(parsed as Record<string, unknown>) };
}
